import React, { useEffect, useState } from "react";
import { getSugerencias } from "../services/API";
import Layout from "./Layout";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Box,
    Typography,
    Chip,
    IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import "bootstrap/dist/css/bootstrap.min.css";
import "../assets/css/sugerencias.css";
import alertify from "alertifyjs";
import "alertifyjs/build/css/alertify.css";
import "alertifyjs/build/css/themes/default.css";
import 'bootstrap/dist/css/bootstrap.min.css';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
        backgroundColor: theme.palette.action.hover,
    },
    "&:last-child td, &:last-child th": {
        border: 0,
    },
    "&:hover": {
        backgroundColor: theme.palette.action.selected,
    },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: "bold",
    padding: "12px 16px",
}));

const MODALIDAD_MAP = {
    1: "Respective Ad Group",
    2: "All Ad Groups",
    3: "Select Ad Groups",
    4: "Respective Campaign",
    5: "All Campaigns",
    6: "Select Campaigns",
    7: "Respective Portfolio",
    8: "All Portfolios",
    9: "Select Portfolios",
    10: "Respective ASIN",
    11: "All ASINs",
    12: "Select ASINs",
};

function Sugerencias() {
    const [sugerencias, setSugerencias] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedAccountLabel, setSelectedAccountLabel] = useState("");

    useEffect(() => {
        const storedLabel = localStorage.getItem("selectedAccountLabel");
        if (storedLabel) {
            setSelectedAccountLabel(storedLabel);
        } else {
            setError("No hay una cuenta seleccionada.");
            setLoading(false);
            return;
        }

        const fetchSugerenciasData = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await getSugerencias();
                console.log("Respuesta de getSugerencias:", response);

                if (response && Array.isArray(response.data)) {
                    const filteredSugerencias = response.data.filter(
                        (sug) => sug.ads_account === storedLabel
                    );
                    setSugerencias(filteredSugerencias);
                } else {
                    console.error("Formato de datos de sugerencias inesperado:", response);
                    setSugerencias([]);
                    setError("Formato de datos inesperado recibido del servidor.");
                }
            } catch (err) {
                console.error("Error al obtener sugerencias:", err);
                setError(
                    "Error al cargar las sugerencias. Por favor, intente de nuevo más tarde."
                );
                setSugerencias([]);
            } finally {
                setLoading(false);
            }
        };

        fetchSugerenciasData();
    }, []);

    const handleDelete = (sugerenciaId) => {
        alertify.confirm(
            "Confirmar Eliminación",
            "¿Estás seguro de que quieres eliminar la sugerencia",
            () => {
                setSugerencias(sugerencias.filter(sug => sug.id !== sugerenciaId));
                alertify.success(`Sugerencia ${sugerenciaId} eliminada.`);
            },
            () => {
                alertify.error("Eliminación de sugerencia cancelada.");
            }
        ).set("labels", { ok: "Sí, eliminar", cancel: "Cancelar" });
    };

    return (
        <Layout>
            <div className="app-container">
                <Box className="container mt-4" sx={{ padding: 3 }}>
                    <Typography variant="h4" component="h2" gutterBottom sx={{ color: "primary.dark", mb: 4 }}>
                        Sugerencias para la cuenta: {selectedAccountLabel}
                    </Typography>

                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
                            <CircularProgress />
                            <Typography variant="h6" sx={{ ml: 2 }}>Cargando sugerencias...</Typography>
                        </Box>
                    ) : error ? (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px", flexDirection: "column" }}>
                            <Typography variant="h6" color="error" gutterBottom>{error}</Typography>
                            <Typography variant="body1" color="text.secondary">
                                Hubo un problema al cargar los datos. Verifique su conexión o intente más tarde.
                            </Typography>
                        </Box>
                    ) : sugerencias.length > 0 ? (
                        <TableContainer component={Paper} elevation={3} sx={{ borderRadius: "8px", overflow: "hidden" }}>
                            <Table sx={{ minWidth: 750 }} aria-label="tabla de sugerencias">
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell>#</StyledTableCell>
                                        <StyledTableCell align="left">Usuario</StyledTableCell>
                                        <StyledTableCell align="left">Cuenta de Anuncios</StyledTableCell>
                                        <StyledTableCell align="left">Fecha Reporte</StyledTableCell>
                                        <StyledTableCell align="left">CST</StyledTableCell>
                                        <StyledTableCell align="left">Modalidad</StyledTableCell>
                                        <StyledTableCell align="left">Recomendación</StyledTableCell>
                                        <StyledTableCell align="center">Acciones</StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sugerencias.map((sug, index) => {
                                        const modalidadText = MODALIDAD_MAP[sug.modalidad] || "Desconocida";
                                        return (
                                            <StyledTableRow key={sug.id || index}>
                                                <TableCell component="th" scope="row">
                                                    {index + 1}
                                                </TableCell>
                                                <TableCell align="left">{sug.usuario}</TableCell>
                                                <TableCell align="left">{sug.ads_account}</TableCell>
                                                <TableCell align="left">{sug.fecha}</TableCell>
                                                <TableCell align="left">{sug.CST}</TableCell>
                                                <TableCell align="left">
                                                    <Chip label={modalidadText} color="primary" size="small" variant="outlined" />
                                                </TableCell>
                                                <TableCell align="left">Negativizar</TableCell>
                                                <TableCell align="center">
                                                    <IconButton
                                                        aria-label="eliminar"
                                                        color="error"
                                                        onClick={() => handleDelete(sug.id)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </StyledTableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px", flexDirection: "column", border: "2px dashed #ccc", borderRadius: "8px", p: 3 }}>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                No hay sugerencias para esta cuenta
                            </Typography>
                            <Typography variant="body2" color="text.disabled">
                                Parece que aún no hay recomendaciones para la cuenta seleccionada.
                            </Typography>
                        </Box>
                    )}
                </Box>
            </div>
        </Layout>
    );
}

export default Sugerencias;